/**
 * Master list of every pane the dashboard can render. The labels here are
 * the single source of truth for the pane menu, drag-and-drop wrapper, and
 * layout customizer dialog. New panes should also be wired into
 * `LazyPaneRenderer.jsx` so they actually render.
 */
export const getAvailablePanes = (language = 'en') => [
  { id: 'bible', label: language === 'es' ? 'Lector Bíblico' : 'Bible Reader' },
  { id: 'votd', label: language === 'es' ? 'Versículo del Día' : 'Verse of the Day' },
  { id: 'companion', label: language === 'es' ? 'Compañero Lumera' : 'Lumera Companion' },
  { id: 'videos', label: language === 'es' ? 'Videos' : 'Videos' },
  { id: 'reading-plans', label: language === 'es' ? 'Planes de Lectura' : 'Reading Plans' },
  { id: 'highlights', label: language === 'es' ? 'Resaltados' : 'Highlights' },
  { id: 'verse-image', label: language === 'es' ? 'Imágenes de Versículos' : 'Verse Images' },
  { id: 'parallel', label: language === 'es' ? 'Vista Paralela' : 'Parallel View' },
  { id: 'comparison', label: language === 'es' ? 'Comparación de Versículos' : 'Verse Comparison' },
  { id: 'interlinear', label: language === 'es' ? 'Vista Interlineal' : 'Interlinear View' },
  { id: 'concordance', label: language === 'es' ? 'Concordancia' : 'Concordance' },
  { id: 'dictionary', label: language === 'es' ? 'Diccionario' : 'Dictionary' },
  { id: 'characters', label: language === 'es' ? 'Personajes' : 'Characters' },
  { id: 'devotional', label: language === 'es' ? 'Devocionales' : 'Devotionals' },
  { id: 'harmony', label: language === 'es' ? 'Armonía de los Evangelios' : 'Gospel Harmony' },
  { id: 'maps', label: language === 'es' ? 'Mapas Bíblicos' : 'Bible Maps' },
  { id: 'chronology', label: language === 'es' ? 'Mapas Cronológicos' : 'Chronological Maps' },
  { id: 'commentary', label: language === 'es' ? 'Comentarios' : 'Commentary' },
  { id: 'profile', label: language === 'es' ? 'Perfil' : 'Profile' },
  { id: 'notes', label: language === 'es' ? 'Mis Notas' : 'My Notes' },
  { id: 'study-paths', label: language === 'es' ? 'Mis Rutas de Estudio' : 'My Study Paths' },
  { id: 'wordstudy', label: language === 'es' ? 'Estudio de Palabras' : 'Word Study' },
  { id: 'crossrefs', label: language === 'es' ? 'Referencias Cruzadas' : 'Cross-References' },
  { id: 'bookmarks', label: language === 'es' ? 'Marcadores' : 'Bookmarks' },
  { id: 'ai', label: language === 'es' ? 'Perspectivas IA' : 'AI Insights' },
  { id: 'topical', label: language === 'es' ? 'Biblia Temática (Nave)' : "Topical Bible (Nave's)" },
  { id: 'atlas', label: language === 'es' ? 'Atlas Bíblico' : 'Bible Atlas' },
  { id: 'historic-plans', label: language === 'es' ? 'Planes Históricos' : 'Historic Reading Plans' },
  { id: 'apocrypha', label: language === 'es' ? 'Apócrifos (KJV 1611)' : 'Apocrypha (KJV 1611)' },
  { id: 'original-languages', label: language === 'es' ? 'Idiomas Originales (GR/LXX)' : 'Original Languages (GR/LXX)' },
  { id: 'isbe', label: language === 'es' ? 'Enciclopedia (ISBE)' : 'Bible Encyclopedia (ISBE)' },
];

/**
 * Grouped view of the panes so the Study Tools drawer / layout customizer can
 * show tidy sections instead of one long flat list. `getAvailablePanes` stays
 * the flat source of truth; this just buckets those same ids into categories.
 */
export const getPaneGroups = (language = 'en') => {
  const all = getAvailablePanes(language);
  const byId = Object.fromEntries(all.map((p) => [p.id, p]));
  const es = language === 'es';
  const groups = [
    { key: 'read', label: es ? 'Leer y Comparar' : 'Read & Compare',
      ids: ['bible', 'votd', 'parallel', 'comparison', 'interlinear', 'original-languages', 'apocrypha'] },
    { key: 'study', label: es ? 'Estudio y Referencia' : 'Study & Reference',
      ids: ['concordance', 'dictionary', 'wordstudy', 'crossrefs', 'commentary', 'topical', 'isbe'] },
    { key: 'ai', label: es ? 'IA y Compañero' : 'AI & Companion',
      ids: ['companion', 'ai'] },
    { key: 'devotion', label: es ? 'Devoción y Planes' : 'Devotion & Plans',
      ids: ['devotional', 'reading-plans', 'historic-plans', 'harmony'] },
    { key: 'maps', label: es ? 'Mapas e Historia' : 'Maps & History',
      ids: ['maps', 'chronology', 'atlas', 'characters'] },
    { key: 'library', label: es ? 'Mi Biblioteca' : 'My Library',
      ids: ['notes', 'study-paths', 'highlights', 'bookmarks', 'verse-image'] },
    { key: 'media', label: es ? 'Medios y Cuenta' : 'Media & Account',
      ids: ['videos', 'profile'] },
  ];
  return groups.map((g) => ({
    key: g.key,
    label: g.label,
    panes: g.ids.map((id) => byId[id]).filter(Boolean),
  }));
};
